import type {
  TextToMusicPlaceholderResponse,
  UploadUserLoopResponse,
} from 'services/api/responses/responseTypes';
import type {
  BundlesPage,
  StoreSongStructures,
  VegaBundle,
} from 'services/store/ai/types';
import { awsApi, baseApi } from '../base';
import { IGenre, IInfoGuide } from '../entities';
import { getPromptFormData } from './helpers';
import type { AIGeneratorPromptData } from './helpers/getPromptFormData/types';
import {
  transformBundleResponse,
  transformBundlesResponse,
  transformGenresResponse,
  transformInfoGuideResponse,
  transformSongStructuresResponse,
  transformTextToMusicPlaceholderResponse,
  transformToError,
} from './transformers';

export const BUNDLES_PER_PAGE = 10;

const aiMusicGeneratorApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    createBundle: build.mutation<VegaBundle, AIGeneratorPromptData>({
      query: (prompt) => {
        const body = getPromptFormData(prompt);
        return {
          method: 'POST',
          url: '/ai/vega/bundles',
          body,
          formData: true,
        };
      },
      transformResponse: transformBundleResponse,
    }),
    getBundleById: build.query<VegaBundle, VegaBundle['id']>({
      query: (id) => `/ai/vega/bundles/${id}?limit=${BUNDLES_PER_PAGE}`,
      transformResponse: transformBundleResponse,
    }),
    getBundles: build.query<
      BundlesPage,
      { page?: number; lastBundleId?: VegaBundle['id'] }
    >({
      query: ({ page, lastBundleId }) => {
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        if (lastBundleId) {
          params.append('last_bundle_id', lastBundleId.toString());
        }
        params.append('limit', BUNDLES_PER_PAGE.toString());
        return `/ai/vega/bundles?${params.toString()}`;
      },
      transformResponse: transformBundlesResponse,
    }),
    getAiMusicGeneratorGenres: build.query<Array<IGenre>, void>({
      query: () => ({
        url: `/ai/vega/genres`,
      }),
      transformResponse: transformGenresResponse,
    }),
    getAiMusicGeneratorSongStructures: build.query<StoreSongStructures, void>({
      query: () => ({
        url: `/ai/vega/structures`,
      }),
      transformResponse: transformSongStructuresResponse,
    }),
    getInstruments: build.query<string[], void>({
      query: () => 'ai/vega/user-audio/labels',
    }),
    uploadUserLoop: build.mutation<
      UploadUserLoopResponse,
      { file: File; instrument: string }
    >({
      query: ({ file, instrument }) => {
        const bodyFormData = new FormData();
        bodyFormData.append('user_audio_file', file);
        bodyFormData.append('user_audio_label', instrument);
        return {
          method: 'POST',
          url: '/ai/vega/user-audio',
          body: bodyFormData,
          formData: true,
        };
      },
      transformErrorResponse: transformToError,
    }),
  }),
  overrideExisting: false,
});

export const aiMusicGeneratorAwsApi = awsApi.injectEndpoints({
  endpoints: (build) => ({
    getInfoGuide: build.query<Array<IInfoGuide>, void>({
      query: () => ({
        url: `/ai_generator/info_guide_dev.json`,
      }),
      transformResponse: transformInfoGuideResponse,
    }),
    getTextToMusicPlaceholder: build.query<
      TextToMusicPlaceholderResponse['prompts'],
      void
    >({
      query: () => ({
        url: `/ai_generator/prompt_list.json`,
      }),
      transformResponse: transformTextToMusicPlaceholderResponse,
    }),
  }),
  overrideExisting: false,
});

export default aiMusicGeneratorApi;
